import React, { useEffect, useRef, useState } from 'react';
import { NumberFormatProps } from 'react-number-format';
import OverrideInput, { OverrideInputProps } from '../OverrideInput/OverrideInput';
import isNil from 'lodash/isNil';

export type OverrideMetadata = {
  effectiveValue?: string | number | Date | null;
  overrideValue?: string | number | Date | null;
  fallbackValue?: string | number | Date | null;
  overridden: boolean;
};

export type OverrideMetadataInputProps = Omit<OverrideInputProps, 'onChange' | 'onRevert' | 'value'> & {
  metadata: OverrideMetadata;
  defaultHandler?: (value?: OverrideMetadata) => void;
  onChange?: (value?: OverrideMetadata) => void;
  onRevert?: (value?: OverrideMetadata) => void;
  parser?: (value: string | number | null) => string | number | null;
  value?: OverrideMetadata;
  watchFieldValue?: string | number | null;
  MaskedProps?: NumberFormatProps;
  forceHelperText?: boolean;
};

export const OverrideMetadataInput = (props: OverrideMetadataInputProps) => {
  const {
    defaultHandler,
    onChange,
    onRevert,
    value,
    metadata,
    parser,
    error,
    helperText,
    watchFieldValue, // Note this is used for updating the current component value state when another field value change affects this components value, consider making this required.
    forceHelperText,
    ...inputProps
  } = props;

  const previousMetadata = useRef(metadata);
  const previousWatchValue = useRef(watchFieldValue);

  const doParsing = (value: string | number | Date | null): string | number | Date | null => {
    const isDate = value instanceof Date;
    if (parser && !isDate) return parser(value);
    return value;
  };

  const overrideValue = (fallbackValue?: string | number | Date | null | undefined): string | number | null => {
    if (!fallbackValue) return null;
    return typeof fallbackValue === 'string' ? '' : 0;
  };

  const getStateFromParsedValue = (parsedValue: string | number | Date | null): OverrideMetadata => {
    const valueToPersist = overrideValue(metadata?.fallbackValue);

    return isNil(parsedValue)
      ? {
          overrideValue: valueToPersist,
          overridden: true,
          effectiveValue: valueToPersist,
          fallbackValue: metadata?.fallbackValue,
        }
      : {
          overrideValue: parsedValue,
          overridden: !isNil(parsedValue),
          effectiveValue: parsedValue,
          fallbackValue: metadata?.fallbackValue,
        };
  };

  const parseData = (data: OverrideMetadata, defaultValue: string | null): OverrideMetadata => {
    const parsedOverrideValue = doParsing(data?.overrideValue ?? defaultValue);
    const parsedEffectiveValue = doParsing(data?.effectiveValue ?? defaultValue);
    const parsedFallbackValue = doParsing(data?.fallbackValue ?? null); // this should always default to null

    return {
      overrideValue: parsedOverrideValue,
      overridden: data?.overridden ?? false,
      effectiveValue: parsedEffectiveValue,
      fallbackValue: parsedFallbackValue,
    };
  };

  const [state, setState] = useState<OverrideMetadata>(parseData(metadata, null));

  const displayedHelperText = forceHelperText
    ? helperText
    : state.overridden && helperText && state.overrideValue !== '';

  // Note: This is used for updating the current component value state when another field value change affects this component value
  // ie. insured lender language type changes will affect the value of proposed loan insured
  useEffect(() => {
    if (metadata && metadata !== previousMetadata.current) {
      setState(parseData(metadata, ''));
      previousMetadata.current = metadata;
    } else if (previousWatchValue.current !== watchFieldValue) {
      setState(parseData(metadata, ''));
      previousWatchValue.current = watchFieldValue;
    }
  }, [metadata]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | Date | string): void => {
    const isDate = event instanceof Date;
    const isObject = typeof event === 'object';
    const shouldParse = isObject && !isDate;
    const processedValue = shouldParse ? doParsing(event?.currentTarget?.value) : event;
    const newState = getStateFromParsedValue(processedValue);
    setState(newState);
    if (onChange) onChange(newState);
    else if (defaultHandler) defaultHandler(newState);
  };

  const handleRevert = (): void => {
    const newState = {
      overridden: false,
      effectiveValue: metadata?.fallbackValue,
      overrideValue: null,
      fallbackValue: metadata?.fallbackValue,
    };
    setState(newState);
    if (onRevert) onRevert(newState);
    else if (defaultHandler) defaultHandler(newState);
  };

  return (
    <>
      <OverrideInput
        {...inputProps}
        error={state.overridden && error && state.overrideValue !== ''}
        helperText={displayedHelperText}
        hideOverrideStyles={Boolean(props.disabled)}
        isOverridden={state.overridden}
        onChange={handleChange}
        onRevert={handleRevert}
        sourceValue={state.fallbackValue}
        value={state.effectiveValue}
      />
    </>
  );
};

export default OverrideMetadataInput;
